import { memo, useCallback, useMemo, useState } from 'react';
import { AlertBanner, SearchInput, Skeleton, StatusMessage } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Users } from 'lucide-react';

import { GROUPS_DO_NOT_GRANT_ACCESS_KEY } from '../../constants/copy.js';
import { useInviteMember } from '../../hooks/useInviteMember';
import GroupMembersPreview from './GroupMembersPreview';
import SelectableCard from './SelectableCard';

/**
 * Groups step (Member only, optional): searchable group list. Groups organize people; they do
 * not grant access.
 *
 * @param {object} props
 * @param {Array<{ id: string, name: string, description: string, memberCount: number }>} props.groups
 * @param {boolean} [props.loading] Groups are still loading.
 * @param {boolean} [props.error] Groups could not be loaded.
 */
function GroupsStep({ groups, loading = false, error = false }) {
  const { t } = useTranslation('people-access');
  const { state, toggleGroup } = useInviteMember();
  const [query, setQuery] = useState('');

  const handleChange = useCallback((event) => setQuery(event.target.value), []);
  const handleClear = useCallback(() => setQuery(''), []);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return groups;
    return groups.filter((g) => `${g.name} ${g.description}`.toLowerCase().includes(needle));
  }, [groups, query]);

  return (
    <section className="flex flex-col gap-3">
      <div>
        <h2 className="m-0 text-sm font-semibold text-content">
          {t('invite.groups.label')}{' '}
          <span className="font-normal text-content-muted">{t('invite.people.optional')}</span>
        </h2>
        <p className="m-0 text-xs text-content-muted">{t(GROUPS_DO_NOT_GRANT_ACCESS_KEY)}</p>
      </div>

      {loading && <Skeleton height="72px" borderRadius="8px" />}
      {error && (
        <AlertBanner tone="danger" title={t('invite.options.errorTitle')}>
          {t('invite.options.errorBody')}
        </AlertBanner>
      )}
      {!loading && !error && groups.length === 0 && (
        <StatusMessage icon={Users} title={t('invite.groups.empty')} />
      )}

      {!loading && !error && groups.length > 0 && (
        <>
          <div className="max-w-xs">
            <SearchInput
              aria-label={t('invite.groups.searchLabel')}
              placeholder={t('invite.groups.searchPlaceholder')}
              value={query}
              onChange={handleChange}
              onClear={handleClear}
            />
          </div>
          {visible.length === 0 ? (
            <p className="m-0 text-sm text-content-muted">{t('invite.groups.noResults')}</p>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {visible.map((group) => (
                <SelectableCard
                  key={group.id}
                  value={group.id}
                  selected={state.groupIds.includes(group.id)}
                  ariaLabel={t('invite.groups.selectGroup', { name: group.name })}
                  onSelect={toggleGroup}
                >
                  <span className="text-sm font-semibold text-content">{group.name}</span>
                  <p className="m-0 mt-1 text-xs text-content-muted">{group.description}</p>
                  <div className="mt-2">
                    <GroupMembersPreview members={group.members} count={group.memberCount} />
                  </div>
                </SelectableCard>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}

export default memo(GroupsStep);
